import { get } from 'aws-amplify/api'
import { getCachedAuthSession } from 'services/authSessionCache'
import ApiError from 'services/ApiError'

let serverErrorFn: (errorMessage: string) => void = () => {}
export const setServerErrorFn = (fn: (errorMessage: string) => void) => {
  serverErrorFn = fn
}

type Props = {
  path: string
  showBannerError?: boolean
}

const processResponse = (res: { statusCode: number, body: { json: () => unknown } }, showBannerError: boolean) => {
  const status = res.statusCode

  if (status >= 500) {
    if (showBannerError) { serverErrorFn('SERVER ERROR') }
    throw new ApiError('Server Error', { status } as Response)
  } else if (status > 401 && status < 500) {
    throw new ApiError('Server Error', { status } as Response)
  } else if (status === 401) {
    throw new ApiError('Unauthorized', { status } as Response)
  } else if (status === 400) {
    return Promise.reject(new ApiError('Bad Request', { status } as Response))
  } else if (status === 204 || status === 304) {
    return null
  }

  return res.body.json()
}

const fetchFromDwaApi = async ({ path, showBannerError = true }: Props) => {
  const session = await getCachedAuthSession()

  const res = await get({
    apiName: 'dwaApi',
    path,
    options: {
      headers: { 'Authorization': session?.tokens?.idToken?.toString() || '' }
    }
  }).response

  return processResponse(res as unknown as { statusCode: number, body: { json: () => unknown } }, showBannerError)
}

const DwaApi = {
  ActiveAlerts: {
    get: (deviceId: string) => fetchFromDwaApi({ path: `/active-alarms/${deviceId}` })
  },
  AlertHistory: {
    get: (deviceId: string, startDate: string, endDate: string, timezone: string) =>
      fetchFromDwaApi({ path: `/alert-history/${deviceId}?startDate=${startDate}&endDate=${endDate}&timezone=${timezone}` })
  },
  AwairRth: {
    get: (deviceId: string, startDate: string, endDate: string, timezone: string, awairDeviceId: string) =>
      fetchFromDwaApi({ path: `/awair-rth/${deviceId}?startDate=${startDate}&endDate=${endDate}&timezone=${timezone}&awairDevice=${awairDeviceId}` })
  },
  ConsumerRth: {
    get: (deviceId: string, granularity: string) =>
      fetchFromDwaApi({ path: `/consumer-rth/${deviceId}/${granularity}` })
  },
  DemandResponse: {
    get: (deviceId: string) => fetchFromDwaApi({ path: `/demand-response?deviceId=${deviceId}`, showBannerError: false })
  },
  HomeownerNotifiedAlerts: {
    get: (deviceId: string, startDate: string, endDate: string, timezone: string) =>
      fetchFromDwaApi({ path: `/home-owner-notified/${deviceId}?startDate=${startDate}&endDate=${endDate}&timezone=${timezone}` })
  },
  RuntimeHistory: {
    get: (deviceId: string, startDate: string, endDate: string, timezone: string) =>
      fetchFromDwaApi({ path: `/rth/${deviceId}?startDate=${startDate}&endDate=${endDate}&timezone=${timezone}` })
  },
  Warranty: {
    get: (serialNumber: string) => fetchFromDwaApi({ path: `/warranties/${serialNumber}` })
  }
}

export default DwaApi
